import React, { useEffect, useState } from 'react';
import { CreditCard, Edit, Save, XCircle, XOctagon, Trash2 } from 'react-feather';
import { api, User } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme, ThemePreference } from '../context/ThemeContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useSnackbar } from '../context/SnackbarContext';

type EditField = 'name' | 'email' | 'password';

const Profile: React.FC = () => {
  const { user: authUser, refreshUser, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const { showSnackbar } = useSnackbar();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editField, setEditField] = useState<EditField | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editConfirm, setEditConfirm] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [billingSubmitting, setBillingSubmitting] = useState(false);

  const authUserId = authUser?.id;

  useEffect(() => {
    if (!authUserId) return;
    api.getSelfUser()
      .then(u => { setUser(u); setLoading(false); })
      .catch(err => { setError(err instanceof Error ? err.message : 'Failed to load profile'); setLoading(false); });
  }, [authUserId]);

  const openEdit = (field: EditField) => {
    setEditField(field);
    setEditValue(field === 'password' ? '' : (field === 'name' ? (user?.name ?? '') : (user?.email ?? '')));
    setEditConfirm('');
  };

  const closeEdit = () => {
    setEditField(null);
    setEditConfirm('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !editField) return;
    if (!editValue.trim()) {
      showSnackbar('This field cannot be empty.', { variant: 'error' });
      return;
    }
    if (editField === 'password' && editValue !== editConfirm) {
      showSnackbar('Passwords do not match.', { variant: 'error' });
      return;
    }

    try {
      setEditSubmitting(true);
      const updated = await api.updateUser(user.id, { [editField]: editValue.trim() });
      setUser(updated);
      refreshUser(updated);
      setEditField(null);
      showSnackbar('Profile updated.', { variant: 'success' });
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to update', { variant: 'error' });
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteSubmitting(true);
    try {
      await api.deleteUser(user.id);
      showSnackbar('Account deleted.', { variant: 'success' });
      logout();
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to delete account', { variant: 'error' });
      setDeleteSubmitting(false);
    }
  };

  const openDeleteConfirm = () => {
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteSubmitting) return;
    setShowDeleteConfirm(false);
  };

  const redirectToBilling = async () => {
    if (!user) return;

    setBillingSubmitting(true);
    try {
      // The frontend only asks for the desired billing action. Stripe-specific
      // customer and subscription details stay behind the backend provider API.
      const session = user.plan === 'pro'
        ? await api.createBillingPortalSession()
        : await api.createBillingCheckoutSession('monthly');
      window.location.assign(session.url);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to open billing', { variant: 'error' });
      setBillingSubmitting(false);
    }
  };

  const formatDate = (val?: string) =>
    val ? new Date(val).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  const fieldLabel: Record<EditField, string> = { name: 'Username', email: 'Email address', password: 'Password' };

  if (loading) return <div className="loading">Loading...</div>;
  if (error || !user) return <div className="container"><div className="error">{error ?? 'User not found'}</div></div>;

  return (
    <div className="container">
      <h2 className="profile-heading">Profile</h2>
      <div className="profile-card">
        <dl className="profile-fields">
          <div className="profile-row profile-row-editable">
            <dt>Username</dt>
            <dd>
              <span>{user.name ?? <span className="profile-empty">not set</span>}</span>
              <button className="btn-icon" title="Edit username" onClick={() => openEdit('name')}><Edit size={14} /></button>
            </dd>
          </div>
          <div className="profile-row profile-row-editable">
            <dt>Email</dt>
            <dd>
              <span>{user.email}</span>
              <button className="btn-icon" title="Edit email" onClick={() => openEdit('email')}><Edit size={14} /></button>
            </dd>
          </div>
          {user.pending_email && (
            <div className="profile-row profile-row-readonly">
              <dt>Pending email</dt>
              <dd><span className="profile-pending-email">{user.pending_email} <span className="profile-pending-note">(awaiting confirmation)</span></span></dd>
            </div>
          )}
          <div className="profile-row profile-row-editable">
            <dt>Password</dt>
            <dd>
              <span className="profile-password">••••••••</span>
              <button className="btn-icon" title="Change password" onClick={() => openEdit('password')}><Edit size={14} /></button>
            </dd>
          </div>
          <div className="profile-row profile-row-readonly">
            <dt>Plan</dt>
            <dd>
              <span className="profile-plan">{user.plan === 'pro' ? 'Pro' : 'Free'}</span>
              <button
                className="btn btn-secondary btn-sm profile-billing-btn"
                onClick={() => void redirectToBilling()}
                disabled={billingSubmitting}
              >
                <CreditCard size={13} />
                {billingSubmitting ? 'Opening...' : user.plan === 'pro' ? 'Manage billing' : 'Upgrade'}
              </button>
            </dd>
          </div>
          <div className="profile-row profile-row-readonly">
            <dt>Member since</dt>
            <dd><span>{formatDate(user.created_at)}</span></dd>
          </div>
          <div className="profile-row profile-row-readonly">
            <dt>Last updated</dt>
            <dd><span>{formatDate(user.updated_at)}</span></dd>
          </div>
          <div className="profile-row profile-row-readonly">
            <dt>Appearance</dt>
            <dd>
              <div className="theme-switcher">
                {(['system', 'light', 'dark'] as ThemePreference[]).map((p) => (
                  <button
                    key={p}
                    className={`theme-btn${preference === p ? ' theme-btn--active' : ''}`}
                    onClick={() => setPreference(p)}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </dd>
          </div>
        </dl>
        <div className="profile-danger-zone">
          <button
            className="btn btn-danger btn-sm"
            onClick={openDeleteConfirm}
            disabled={deleteSubmitting}
          >
            <Trash2 size={13} style={{ marginRight: '0.35rem' }} />
            Delete account
          </button>
        </div>
      </div>

      {editField && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit {fieldLabel[editField]}</h2>
              <button className="btn-close" onClick={closeEdit}><XCircle size={18} /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-profile-field">{editField === 'password' ? 'New password' : fieldLabel[editField]}</label>
                <input
                  id="edit-profile-field"
                  type={editField === 'password' ? 'password' : editField === 'email' ? 'email' : 'text'}
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  autoFocus
                />
              </div>
              {editField === 'password' && (
                <div className="form-group">
                  <label htmlFor="edit-profile-confirm">Repeat new password</label>
                  <input
                    id="edit-profile-confirm"
                    type="password"
                    value={editConfirm}
                    onChange={e => setEditConfirm(e.target.value)}
                  />
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeEdit} disabled={editSubmitting}>
                  <XOctagon size={14} />
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editSubmitting}>
                  <Save size={14} />
                  {editSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <DeleteConfirmationModal
          title="Delete account"
          message="Deleting your account permanently removes your profile and all data tied to it, including questions, variables, and data points."
          confirmLabel="Delete account"
          submitting={deleteSubmitting}
          inputId="delete-account-confirmation"
          onCancel={closeDeleteConfirm}
          onConfirm={() => void handleDeleteAccount()}
        />
      )}
    </div>
  );
};

export default Profile;
